import {
  PatientTargetModel,
  PatientTargetStatus,
  PatientProgramModel,
  PatientProgramStatus,
  TargetFailOption,
  TargetType,
  PatientTargetDataModel,
  PatientBehaviorModel,
  AlarmOption,
  SkillAcquisitionObjectiveModel,
} from '../../core/api/models';

export function getPatientTargetStatuses() {
  const targetStatuses = [
    { value: PatientTargetStatus.Ready, text: 'Ready' },
    { value: PatientTargetStatus.Baseline, text: 'Baseline' },
    { value: PatientTargetStatus.Aquisition, text: 'Acquisition' },
    { value: PatientTargetStatus.Mastered, text: 'Mastered' },
    { value: PatientTargetStatus.Maintenance, text: 'Maintenance' },
    { value: PatientTargetStatus.Closed, text: 'Closed' },
    { value: PatientTargetStatus.On_Hold, text: 'On Hold' },
    { value: PatientTargetStatus.Fluent, text: 'Fluent' },
  ];
  return targetStatuses;
}

export function getPatientProgramStatuses() {
  const programStatuses = [
    { value: PatientProgramStatus.Ready, text: 'Ready' },
    { value: PatientProgramStatus.Baseline, text: 'Baseline' },
    { value: PatientProgramStatus.Aquisition, text: 'Acquisition' },
    { value: PatientProgramStatus.Mastered, text: 'Mastered' },
    { value: PatientProgramStatus.Maintenance, text: 'Maintenance' },
    { value: PatientProgramStatus.Closed, text: 'Closed' },
    { value: PatientProgramStatus.On_Hold, text: 'On Hold' },
  ];
  return programStatuses;
}

export function getBspCurrentObjectiveDropdownLabel(
  objective: SkillAcquisitionObjectiveModel
): string {
  const objectiveText =
    normalizeDropdownLabelPart(objective.Goal) ??
    normalizeDropdownLabelPart(objective.PreviousGoal) ??
    'No Objective Description';

  const parts = [
    objective.Program,
    objective.SkillArea,
    objectiveText,
    objective.GoalStatus,
  ]
    .map((part) => normalizeDropdownLabelPart(part))
    .filter((part): part is string => part != null);

  const distinctParts = parts.filter(
    (part, index) =>
      parts.findIndex(
        (existingPart) =>
          existingPart.toLowerCase() === part.toLowerCase()
      ) === index
  );

  return distinctParts.length > 0
    ? distinctParts.join(' - ')
    : 'Unlabeled BSP Objective';
}

export function getAcademyAssessmentObjectiveDropdownLabel(
  objective: SkillAcquisitionObjectiveModel
): string {
  const parts = [objective.SourceKey, objective.Goal, objective.GoalStatus]
    .map((part) => normalizeDropdownLabelPart(part))
    .filter((part): part is string => part != null);

  return parts.length > 0
    ? parts.join(' - ')
    : 'Unlabeled IAP Objective';
}

function normalizeDropdownLabelPart(
  part: string | null | undefined
): string | null {
  const trimmedPart = part?.trim();
  return trimmedPart ? trimmedPart : null;
}

export function getTargetFailOptions() {
  const targetFailOptions = [
    { value: TargetFailOption.MarkAsFailed, text: 'Mark As Failed' },
  ];
  return targetFailOptions;
}

export function targetTypeHasGraph(targetType: TargetType) {
  switch (targetType) {
    case TargetType.Frequency:
    case TargetType.Duration:
    case TargetType.Quantity:
    case TargetType.FluencyRate:
    case TargetType.Echoic:
    case TargetType.DiscreteTrialTeaching:
      return true;
    default:
      return false;
  }
}

export function getPatientTargetStatusAtAGlanceColor(
  target: PatientTargetModel | undefined
) {
  if (target?.TargetStatus === PatientTargetStatus.Maintenance) {
    return '#cc9e7f';
  } else if (isTargetActive(target)) {
    return '#8AC476';
  } else if (target?.TargetStatus === PatientTargetStatus.Ready) {
    return '#dfe2af';
  } else if (target?.IsArchived) {
    return '#1e365f';
  } else if (target?.TargetStatus === PatientTargetStatus.Mastered) {
    return '#68a1b7';
  } else if (target?.TargetStatus === PatientTargetStatus.On_Hold) {
    return '#c16363';
  } else if (target?.TargetStatus === PatientTargetStatus.Closed) {
    return '#b2b2b2';
  } else if (target?.TargetStatus === PatientTargetStatus.Fluent) {
    return '#b2b2b2';
  } else {
    return '#b2b2b2';
  }
}

export function getPatientTargetStatusAtAGlanceTintColor(
  target: PatientTargetModel | undefined
) {
  if (target?.TargetStatus === PatientTargetStatus.Maintenance) {
    return '#fff';
  } else if (isTargetActive(target)) {
    return '#fff';
  } else if (target?.TargetStatus === PatientTargetStatus.Ready) {
    return '#2c3e50';
  } else if (target?.IsArchived) {
    return '#fff';
  } else if (target?.TargetStatus === PatientTargetStatus.Mastered) {
    return '#fff';
  } else if (target?.TargetStatus === PatientTargetStatus.On_Hold) {
    return '#fff';
  } else if (target?.TargetStatus === PatientTargetStatus.Closed) {
    return '#2c3e50';
  } else if (target?.TargetStatus === PatientTargetStatus.Fluent) {
    return '#2c3e50';
  } else {
    return '#2c3e50';
  }
}

export function getPatientTargetStatusCircleColor(
  target: PatientTargetModel | undefined
) {
  if (target?.TargetStatus === PatientTargetStatus.Maintenance) {
    return '#cc9e7f';
  } else if (isTargetActive(target)) {
    return '#8AC476';
  } else if (target?.TargetStatus === PatientTargetStatus.Ready) {
    return '#dfe2af';
  } else if (target?.IsArchived) {
    return '#1e365f';
  } else if (target?.TargetStatus === PatientTargetStatus.Mastered) {
    return '#68a1b7';
  } else if (target?.TargetStatus === PatientTargetStatus.On_Hold) {
    return '#c16363';
  } else if (target?.TargetStatus === PatientTargetStatus.Closed) {
    return '#b2b2b2';
  } else if (target?.TargetStatus === PatientTargetStatus.Fluent) {
    return '#b2b2b2';
  } else {
    return '#b2b2b2';
  }
}

export function getPatientBehaviorStatusColor(
  behavior: PatientBehaviorModel | undefined
) {
  if (behavior?.IsDeleted == true) {
    return '#b2b2b2';
  } else if (behavior?.IsArchived == true) {
    return '#1e365f';
  } else if (behavior?.IsClosed == true) {
    return '#b2b2b2';
  } else if (behavior?.IsMastered == true) {
    return '#68a1b7';
  } else {
    return '#8AC476';
  }
}

export function getPatientBehaviorStatusTooltip(
  behavior: PatientBehaviorModel | undefined
) {
  if (behavior?.IsDeleted == true) {
    return 'Deleted';
  } else if (behavior?.IsArchived == true) {
    return 'Archived';
  } else if (behavior?.IsClosed == true) {
    return 'Closed';
  } else if (behavior?.IsMastered == true) {
    return 'Mastered';
  } else {
    return 'Active';
  }
}
export function getPatientProgramStatusAtAGlanceColor(
  program: PatientProgramModel | undefined
) {
  if (program?.ProgramStatus === PatientProgramStatus.Maintenance) {
    return '#cc9e7f';
  } else if (isProgramActive(program)) {
    return '#8AC476';
  } else if (program?.ProgramStatus === PatientProgramStatus.Ready) {
    return '#dfe2af';
  } else if (program?.IsArchived) {
    return '#1e365f';
  } else if (program?.ProgramStatus === PatientProgramStatus.Mastered) {
    return '#68a1b7';
  } else if (program?.ProgramStatus === PatientProgramStatus.On_Hold) {
    return '#c16363';
  } else {
    return '#b2b2b2';
  }
}

export function isProgramActive(program: PatientProgramModel | undefined) {
  if (!program) {
    return false;
  }
  return (
    program.ProgramStatus === PatientProgramStatus.Aquisition ||
    program.ProgramStatus === PatientProgramStatus.Baseline ||
    program.ProgramStatus === PatientProgramStatus.Maintenance
  );
}

export const ACTIVE_PROGRAM_FILTER_STATUSES: readonly PatientProgramStatus[] = [
  PatientProgramStatus.Aquisition,
  PatientProgramStatus.Maintenance,
];

export const ACTIVE_TARGET_FILTER_STATUSES: readonly PatientTargetStatus[] = [
  PatientTargetStatus.Aquisition,
  PatientTargetStatus.Maintenance,
];

export function isPatientProgramStatusActiveForQuickFilter(
  status: PatientProgramStatus | null | undefined
): boolean {
  if (status == null) {
    return false;
  }
  return ACTIVE_PROGRAM_FILTER_STATUSES.includes(status);
}

export function isPatientTargetStatusActiveForQuickFilter(
  status: PatientTargetStatus | null | undefined
): boolean {
  if (status == null) {
    return false;
  }
  return ACTIVE_TARGET_FILTER_STATUSES.includes(status);
}

export function filterProgramsForActiveQuickFilter<
  T extends { ProgramStatus?: PatientProgramStatus | null }
>(programs: T[] | null | undefined): T[] {
  return (programs ?? []).filter((program) =>
    isPatientProgramStatusActiveForQuickFilter(program.ProgramStatus)
  );
}

export function filterTargetsForActiveQuickFilter<
  T extends { TargetStatus?: PatientTargetStatus | null }
>(targets: T[] | null | undefined): T[] {
  return (targets ?? []).filter((target) =>
    isPatientTargetStatusActiveForQuickFilter(target.TargetStatus)
  );
}

export function isTargetActive(target: PatientTargetModel | undefined) {
  if (!target) {
    return false;
  }
  return (
    target?.TargetStatus === PatientTargetStatus.Aquisition ||
    target?.TargetStatus === PatientTargetStatus.Baseline ||
    target?.TargetStatus === PatientTargetStatus.Maintenance
  );
}

export function isTargetInactive(target: PatientTargetModel | undefined) {
  //inactive programs would be anything that's not probe, acquisition, maintenance statuses
  if (!target) {
    return false;
  }
  return (
    target?.TargetStatus !== PatientTargetStatus.Aquisition &&
    target?.TargetStatus !== PatientTargetStatus.Baseline &&
    target?.TargetStatus !== PatientTargetStatus.Maintenance
  );
}

export function isTargetFuture(target: PatientTargetModel | undefined) {
  //Future targets would be on hold, and ready
  if (!target) {
    return false;
  }
  return (
    target?.TargetStatus === PatientTargetStatus.On_Hold ||
    target?.TargetStatus === PatientTargetStatus.Ready
  );
}

export function getPatientTargetStatusAtAGlanceText(
  target: PatientTargetModel | undefined
) {
  if (target?.TargetStatus === PatientTargetStatus.Maintenance) {
    return 'Maintenance';
  } else if (target?.TargetStatus === PatientTargetStatus.On_Hold) {
    return 'On Hold';
  } else if (target?.TargetStatus === PatientTargetStatus.Ready) {
    return 'Ready';
  } else if (target?.IsArchived) {
    return 'Archived';
  } else if (target?.TargetStatus === PatientTargetStatus.Mastered) {
    return 'Mastered';
  } else if (target?.TargetStatus === PatientTargetStatus.Baseline) {
    return 'Baseline';
  } else if (target?.TargetStatus === PatientTargetStatus.Aquisition) {
    return 'Acquisition';
  } else if (target?.TargetStatus === PatientTargetStatus.Closed) {
    return 'Closed';
  } else if (target?.TargetStatus === PatientTargetStatus.Fluent) {
    return 'Fluent';
  } else {
    return 'Unknown';
  }
}

export function getPatientProgramStatusAtAGlanceText(
  program: PatientProgramModel | undefined
) {
  if (program?.ProgramStatus === PatientProgramStatus.Maintenance) {
    return 'Maintenance';
  } else if (program?.ProgramStatus === PatientProgramStatus.On_Hold) {
    return 'On Hold';
  } else if (program?.ProgramStatus === PatientProgramStatus.Ready) {
    return 'Ready';
  } else if (program?.IsArchived) {
    return 'Archived';
  } else if (program?.ProgramStatus === PatientProgramStatus.Mastered) {
    return 'Mastered';
  } else if (program?.ProgramStatus === PatientProgramStatus.Baseline) {
    return 'Baseline';
  } else if (program?.ProgramStatus === PatientProgramStatus.Aquisition) {
    return 'Acquisition';
  } else {
    return 'Unknown';
  }
}

export function getPatientTargetStatusLabel(targetStatus: PatientTargetStatus) {
  const targetStatuses = getPatientTargetStatuses();
  const targetStatusResult = targetStatuses.find(
    (ts) => ts.value === targetStatus
  );
  return targetStatusResult?.text ?? 'Unknown';
}

export function getPatientProgramStatusLabel(
  programStatus: PatientProgramStatus
) {
  const programStatuses = getPatientProgramStatuses();
  const programStatusResult = programStatuses.find(
    (ps) => ps.value === programStatus
  );
  return programStatusResult?.text ?? 'Ready';
}

export function getPatientTargetStatusColor(targetStatus: PatientTargetStatus) {
  switch (targetStatus) {
    case PatientTargetStatus.Mastered:
      return '#68a1b7';
    case PatientTargetStatus.Ready:
      return '#dfe2af';
    case PatientTargetStatus.Baseline:
      return '#8AC476';
    case PatientTargetStatus.Aquisition:
      return '#8AC476';
    case PatientTargetStatus.Maintenance:
      return '#cc9e7f';
    case PatientTargetStatus.Closed:
      return '#b2b2b2';
    case PatientTargetStatus.On_Hold:
      return '#c16363';
    case PatientTargetStatus.Fluent:
      return '#b2b2b2';
  }
  return '#FFFFFF';
}

// Extend the Date prototype
declare global {
  interface Date {
    toServerString(): string;
    fromUTCtoServerString(): string;
  }
}

Date.prototype.fromUTCtoServerString = function (): string {
  //Convert the date to the server timezone (which is mountain time) and then to iso string
  const date = new Date(this);

  //Get the timezone offset of the server (Mountain Time)
  const serverTimezoneOffset = getTimezoneOffset('America/Denver');

  const localTimezoneOffset = date.getTimezoneOffset();
  const diffTimezoneOffset = serverTimezoneOffset - localTimezoneOffset;
  date.setMinutes(date.getMinutes() + diffTimezoneOffset);
  return date.toISOString().replace('Z', '');
};

Date.prototype.toServerString = function (): string {
  //Convert the date to the server timezone (which is mountain time) and then to iso string
  const date = new Date(this);

  //Get the timezone offset of the server (Mountain Time)
  const serverTimezoneOffset = getTimezoneOffset('America/Denver');

  const localTimezoneOffset = date.getTimezoneOffset();
  const diffTimezoneOffset = serverTimezoneOffset;
  date.setMinutes(date.getMinutes() + diffTimezoneOffset);
  return date.toISOString().replace('Z', '');
};

export function getTimezoneOffset(timeZone: string, date = new Date()) {
  const utcDate = new Date(
    date.toLocaleString('en-US', { timeZone: 'UTC' })
  ).getTime();
  const tzDate = new Date(date.toLocaleString('en-US', { timeZone })).getTime();
  return (tzDate - utcDate) / 60000;
}

export const MAX_BULK_FREQUENCY_OCCURRENCES = 100;

export function getBulkFrequencyDates(
  startValue: string,
  endValue: string,
  occurrences: number
): Date[] {
  if (!startValue || !endValue || !Number.isInteger(occurrences) || occurrences < 1) {
    throw new Error('Please enter start, end, and a valid whole number of occurrences.');
  }
  if (occurrences > MAX_BULK_FREQUENCY_OCCURRENCES) {
    throw new Error(`Occurrences cannot exceed ${MAX_BULK_FREQUENCY_OCCURRENCES}.`);
  }

  const start = new Date(startValue);
  const end = new Date(endValue);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    throw new Error('Invalid start or end date/time.');
  }
  if (end <= start) {
    throw new Error('End must be after start.');
  }

  const totalSeconds = Math.floor((end.getTime() - start.getTime()) / 1000);
  if (totalSeconds < occurrences) {
    throw new Error('Total duration must be at least the number of occurrences in seconds.');
  }

  const stepSeconds = Math.floor(totalSeconds / occurrences);
  const remainder = totalSeconds - stepSeconds * occurrences;
  const dates: Date[] = [];
  let accumulatedSeconds = 0;

  for (let index = 0; index < occurrences; index++) {
    dates.push(new Date(start.getTime() + accumulatedSeconds * 1000));
    accumulatedSeconds += stepSeconds + (index < remainder ? 1 : 0);
  }

  return dates;
}

export function formatLocalDateTime(date: Date): string {
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 19);
}

export interface TextPositionOption {
  label: string;
  value: string;
  x: number;
  y: number;
}

export const TEXT_POSITION_OPTIONS: TextPositionOption[] = [
  { label: 'Top Left', value: 'top-left', x: 50, y: 50 },
  { label: 'Top Center', value: 'top-center', x: 500, y: 50 },
  { label: 'Top Right', value: 'top-right', x: 950, y: 50 },
  { label: 'Center Left', value: 'center-left', x: 50, y: 175 },
  { label: 'Center Center', value: 'center-center', x: 500, y: 175 },
  { label: 'Center Right', value: 'center-right', x: 950, y: 175 },
  { label: 'Bottom Left', value: 'bottom-left', x: 50, y: 300 },
  { label: 'Bottom Center', value: 'bottom-center', x: 500, y: 300 },
  { label: 'Bottom Right', value: 'bottom-right', x: 950, y: 300 },
];

export function getTextPositionLabel(x: number | null | undefined, y: number | null | undefined): string {
  if (x == null || y == null) {
    return 'x:' + (x ?? 'null') + ' y:' + (y ?? 'null');
  }
  
  const matchingPosition = TEXT_POSITION_OPTIONS.find(
    opt => opt.x === x && opt.y === y
  );
  
  return matchingPosition ? matchingPosition.label : 'x:' + x + ' y:' + y;
}

export const dataURItoBlob: (dataURI: string) => Blob = (dataURI: string) => {
  const binary = atob(dataURI.split(',')[1]);
  const array = [];
  for (let i = 0; i < binary.length; i++) {
    array.push(binary.charCodeAt(i));
  }
  return new Blob([new Uint8Array(array)], { type: 'image/jpeg' });
};

export function targetIsPromptBased(target: PatientTargetModel | undefined) {
  return (
    target?.TargetType === TargetType.DiscreteTrialTeaching ||
    target?.TargetType === TargetType.Echoic
  );
}

export function targetIsEchoic(target: PatientTargetModel | undefined) {
  return target?.TargetType === TargetType.Echoic;
}

export function getAlarmOptions() {
  return [
    { value: AlarmOption.NoAlarm, label: 'No Alarm' },
    { value: AlarmOption.AlarmAtEnd, label: 'Ring at End' },
    {
      value: AlarmOption.AlarmAtThirtySecondsRemaining,
      label: 'Ring at 30 Seconds Remaining',
    },
    {
      value: AlarmOption.AlarmAtOneMinuteRemaining,
      label: 'Ring at 1 Minute Remaining',
    },
  ];
}

export function getAlarmSoundOptions() {
  return [
    { value: 1, label: 'Sound 1' },
    { value: 2, label: 'Sound 2' },
    { value: 3, label: 'Sound 3' },
    { value: 4, label: 'Sound 4' },
  ];
}
